/**
 * Component Selector
 *
 * Dynamically selects the appropriate Add/Charge component based on gateway name.
 *
 * Gateway name format: "{prefix}_{suffix}" (e.g., "ckout_pg", "gpcko_pg")
 * The prefix is used to look up the correct entry in `GATEWAY_ENTRIES`.
 *
 * Mobile routing is **declarative** — each entry optionally declares a `mobileDelegate`
 * config. At module load, a single resolution loop processes all entries, swapping in
 * ChargeMobileDelegate where needed and building `webProcessedKinds` automatically.
 *
 * To add a new gateway:
 * 1. Import the new component(s)
 * 2. Add one entry to `GATEWAY_ENTRIES`
 *    - If it needs mobile delegation: add `mobileDelegate: { humanKind, maxVersion? }`
 *    - If it always delegates to native (like IAP): add `nativeOnly: true`
 *    - Everything else (web-processed kinds, mobile routing) is handled automatically.
 */

import React from 'react';
// Add component (only CC needs a setup screen; other kinds are added directly via API)
import { AddCreditCardCheckoutFrames } from './checkout-frames/AddCreditCardCheckoutFrames';
// Charge components
import { ChargeViaAPI } from './shared/ChargeViaAPI';
import { ChargeGooglePayCheckoutFlow } from './checkout-flow/ChargeGooglePayCheckoutFlow';
import { ChargeApplePay } from './apple-pay/ChargeApplePay';
import { ChargeAppleMerchant } from './apple-pay/ChargeAppleMerchant';
import { ChargePayPal } from './paypal/ChargePayPal';
import { ChargeIAP } from './iap/ChargeIAP';
// Mobile delegation component
import { ChargeMobileDelegate } from './mobile/ChargeMobileDelegate';
import type { ChargeMobileDelegateProps } from './mobile/ChargeMobileDelegate';
// Mobile detection & version gating
import {
  isFromMobileApp,
  isVersionHigherOrEqual,
  SUPPORTED_CC_WEB_PROCESSING_VERSION
} from '../utils/paymentSupportUtils';

import type {
  ChargeProcessorProps,
  AddPaymentSourceProps,
  GatewayConfig,
  PaymentSource,
  PaymentMethodKind
} from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * Declarative gateway entry — everything about a gateway in one place.
 *
 * Mobile behavior is declared here and resolved automatically at module load:
 * - `mobileDelegate` — on mobile, swap chargeComponent for ChargeMobileDelegate
 * - `nativeOnly` — component always delegates to native (e.g., ChargeIAP)
 */
interface GatewayEntry {
  kind: PaymentMethodKind;
  chargeComponent: React.ComponentType<ChargeProcessorProps>;

  /**
   * Mobile delegation. When set, on mobile the charge is delegated to the
   * native app via ChargeMobileDelegate instead of the web chargeComponent.
   *
   * - `humanKind`: label sent to the native app in the `on_pay` message
   * - `minVersion`: minimum app version that supports web processing.
   *    Below this version → delegate to native app.
   *    At or above → use the web chargeComponent.
   *    Omit to always delegate on mobile.
   * - `shouldDelegate`: optional function for custom delegation logic.
   *    When provided and returns true → delegate to native app (regardless of version).
   */
  mobileDelegate?: { humanKind: string; minVersion?: string; shouldDelegate?: () => boolean };

  /**
   * When true, the charge component inherently delegates to the native app
   * (e.g., ChargeIAP sends `on_pay` internally). These kinds are never
   * considered "web-processed" for mobile analytics.
   */
  nativeOnly?: boolean;

  addComponent?: React.ComponentType<AddPaymentSourceProps>;
}

/** Resolved config after mobile routing (consumed by lookup helpers) */
interface ResolvedGatewayConfig {
  kind: PaymentMethodKind;
  chargeComponent: React.ComponentType<ChargeProcessorProps>;
  addComponent?: React.ComponentType<AddPaymentSourceProps>;
}

// ============================================================================
// Mobile Delegate Factory
// ============================================================================

/**
 * Create a ChargeProcessorProps-compatible wrapper around ChargeMobileDelegate
 * for a specific payment method kind.
 */
const createMobileDelegate = (
  paymentKind: string,
  humanKind: string
): React.ComponentType<ChargeProcessorProps> => {
  const MobileDelegateWrapper: React.FC<ChargeProcessorProps> = (props) => (
    <ChargeMobileDelegate
      { ...props as ChargeMobileDelegateProps }
      paymentKind={ paymentKind }
      humanKind={ humanKind }
    />
  );
  MobileDelegateWrapper.displayName = `ChargeMobileDelegate_${ paymentKind }`;
  return MobileDelegateWrapper;
};

// ============================================================================
// Gateway Entries (single source of truth — add new gateways here)
// ============================================================================

const isMobile = isFromMobileApp();

/**
 * All gateway configurations. Each entry declares:
 * - Which components handle Add / Charge
 * - Mobile delegation behavior (optional)
 *
 * The resolution loop below processes these once at module load to produce
 * the final `GATEWAY_PREFIX_REGISTRY` and `webProcessedKinds`.
 */
const GATEWAY_ENTRIES: Record<string, GatewayEntry> = {
  ckout: {
    kind: 'cc',
    chargeComponent: ChargeViaAPI,
    mobileDelegate: {
      humanKind: 'Credit or Debit Card',
      minVersion: SUPPORTED_CC_WEB_PROCESSING_VERSION,
      shouldDelegate: () => !new URLSearchParams(window.location.search).has('credits_requested')
    },
    addComponent: AddCreditCardCheckoutFrames
  },
  gpcko: {
    kind: 'gp',
    chargeComponent: ChargeGooglePayCheckoutFlow
  },
  apcko: {
    kind: 'ap',
    chargeComponent: ChargeApplePay
  },
  amtck: {
    kind: 'am',
    chargeComponent: ChargeAppleMerchant
  },
  pp: {
    kind: 'pp',
    chargeComponent: ChargePayPal,
    mobileDelegate: { humanKind: 'PayPal' }
  },
  iap: {
    kind: 'iap',
    chargeComponent: ChargeIAP,
    nativeOnly: true
  }
};

// ============================================================================
// Module-Level Resolution (runs once — URL params never change)
// ============================================================================

/**
 * Process GATEWAY_ENTRIES to produce the final resolved registry and the set
 * of web-processed kinds. For each entry:
 *
 * 1. If on mobile and `mobileDelegate` is declared:
 *    - Always delegate when no `minVersion`, OR delegate when app version < minVersion
 *    - Also delegate when `shouldDelegate()` returns true (custom logic)
 *    - Swap `chargeComponent` to ChargeMobileDelegate
 * 2. Track whether the kind is web-processed (not delegated and not nativeOnly)
 *    - Used by MobilePurchaseFlow to decide which payments need `on_pay_completed`
 *      and analytics messages sent to the native app
 */
const resolvedRegistry: Record<string, ResolvedGatewayConfig> = {};
const webProcessedKindsSet = new Set<string>();

Object.entries(GATEWAY_ENTRIES).forEach(([prefix, entry]) => {
  const { mobileDelegate, nativeOnly, ...config } = entry;
  let delegated = !!nativeOnly;

  if (isMobile && mobileDelegate) {
    const versionTooOld = !mobileDelegate.minVersion
      || !isVersionHigherOrEqual(mobileDelegate.minVersion);
    const shouldDelegate = versionTooOld || !!mobileDelegate.shouldDelegate?.();

    if (shouldDelegate) {
      config.chargeComponent = createMobileDelegate(entry.kind, mobileDelegate.humanKind);
      delegated = true;
    }
  }

  if (!delegated) {
    webProcessedKindsSet.add(entry.kind);
  }

  resolvedRegistry[prefix] = config;
});

const GATEWAY_PREFIX_REGISTRY: Record<string, ResolvedGatewayConfig> = resolvedRegistry;

/**
 * Check if a payment kind is processed on the web in mobile context.
 * Used by MobilePurchaseFlow to decide whether to send `on_pay_completed`
 * and analytics to the native app.
 */
export const isWebProcessedPayment = (kind?: string): boolean => !!kind && webProcessedKindsSet.has(kind);

// ============================================================================
// Registry Lookup Helpers
// ============================================================================

/**
 * Extract gateway prefix from gateway name.
 * Example: "ckout_pg" -> "ckout", "pp" -> "pp"
 */
const getGatewayPrefix = (gatewayName: string): string => gatewayName.toLowerCase().split('_')[0] || '';

/** Look up prefix config from a gateway name */
const getPrefixConfig = (gatewayName: string): ResolvedGatewayConfig | undefined => GATEWAY_PREFIX_REGISTRY[getGatewayPrefix(gatewayName)];

/** Get Add component for a gateway. Only CC has a setup screen; others return null. */
export const getAddPaymentComponent = (
  gatewayName: string
): React.ComponentType<AddPaymentSourceProps> | null => getPrefixConfig(gatewayName)?.addComponent ?? null;

/**
 * Get Charge component for a gateway.
 *
 * Mobile routing is already resolved at module load time — chargeComponent
 * points to the correct processor (web or native delegation) based on
 * platform and app version. No runtime checks needed here.
 *
 * Quick top-up always uses ChargeViaAPI (server-side reference transaction)
 * regardless of the payment method kind.
 */
export const getChargePaymentComponent = (
  gatewayName: string,
  topupFlow?: string
): React.ComponentType<ChargeProcessorProps> | null => {
  const config = getPrefixConfig(gatewayName);
  if (!config) return null;
  if (topupFlow === 'quick') {
    return ChargeViaAPI;
  }
  return config.chargeComponent;
};

/**
 * Add Payment Component
 */
export interface AddPaymentComponentProps {
  gatewayName: string;
  gatewayConfig: GatewayConfig;
  paymentSourceId: number;
  onSuccess: (paymentSource: PaymentSource) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
  /** Called to re-fetch gateway config (e.g., after error to get fresh tokens) */
  onRefreshConfig?: () => void;
  /** When true, show PAYG disclaimer on add credit card form */
  isPayg?: boolean;
}

export const AddPaymentComponent: React.FC<AddPaymentComponentProps> = ({
  gatewayName,
  gatewayConfig,
  paymentSourceId,
  onSuccess,
  onError,
  onCancel,
  onRefreshConfig,
  isPayg
}) => {
  const Component = getAddPaymentComponent(gatewayName);

  if (!Component) {
    return (
      <div>
        <div>
          Unsupported gateway:
          {gatewayName}
        </div>
      </div>
    );
  }

  return (
    <Component
      gatewayConfig={ gatewayConfig }
      paymentSourceId={ paymentSourceId }
      onSuccess={ onSuccess }
      onError={ onError }
      onCancel={ onCancel }
      onRefreshConfig={ onRefreshConfig }
      isPayg={ isPayg }
    />
  );
};

/**
 * Charge Payment Component
 *
 * Wrapper that selects the appropriate headless processor based on gateway name.
 * All Charge processors render null (or hidden container for Google Pay) and
 * communicate state via onStatusChange, onSuccess, onError, onCancel callbacks.
 */
export interface ChargePaymentComponentProps extends ChargeProcessorProps {
  gatewayName: string;
}

export const ChargePaymentComponent: React.FC<ChargePaymentComponentProps> = ({
  gatewayName,
  ...restProps
}) => {
  // topupFlow is used both for routing (selecting processor) and by the processor itself
  const Component = getChargePaymentComponent(gatewayName, restProps.topupFlow);

  if (!Component) {
    return (
      <div>
        <div>
          Unsupported gateway:
          {gatewayName}
        </div>
      </div>
    );
  }

  return <Component { ...restProps } />;
};
